import React, { useState, useEffect, useCallback } from 'react';
import './QuestionOverlay.css';

function QuestionOverlay({
    question,
    options,
    correctAnswer,
    explanation,
    onAnswer,
    timeLimit = 15,
    xpBonus = 15,
    showTimer = true
}) {
    const [selectedAnswer, setSelectedAnswer] = useState(null);
    const [showResult, setShowResult] = useState(false);
    const [timeLeft, setTimeLeft] = useState(timeLimit);
    const [isCorrect, setIsCorrect] = useState(false);

    const handleTimeout = useCallback(() => {
        setShowResult(true);
        setIsCorrect(false);
        setTimeout(() => {
            onAnswer(false, 0);
        }, 2000);
    }, [onAnswer]);

    useEffect(() => {
        if (!showTimer || showResult) return;

        const timer = setInterval(() => {
            setTimeLeft(prev => {
                if (prev <= 1) {
                    clearInterval(timer);
                    handleTimeout();
                    return 0;
                }
                return prev - 1;
            });
        }, 1000);

        return () => clearInterval(timer);
    }, [showResult, showTimer, handleTimeout]);

    const handleAnswer = (answer) => {
        if (showResult) return;

        setSelectedAnswer(answer);
        const correct = answer === correctAnswer;
        setIsCorrect(correct);
        setShowResult(true);

        setTimeout(() => {
            onAnswer(correct, correct ? xpBonus : 0);
        }, 2000);
    };

    const getOptionClass = (option) => {
        if (!showResult) {
            return selectedAnswer === option ? 'selected' : '';
        }
        if (option === correctAnswer) return 'correct';
        if (option === selectedAnswer && !isCorrect) return 'incorrect';
        return 'dimmed';
    };

    const timerPercent = (timeLeft / timeLimit) * 100;

    return (
        <div className="question-overlay">
            <div className="question-modal">
                <div className="question-header">
                    <span className="question-badge">🎯 Bonus Question!</span>
                    <span className="xp-reward">+{xpBonus} XP</span>
                </div>

                {showTimer && !showResult && (
                    <div className="question-timer">
                        <div
                            className="timer-bar"
                            style={{
                                width: `${timerPercent}%`,
                                backgroundColor: timerPercent > 30 ? '#4CAF50' : timerPercent > 10 ? '#ffc107' : '#f44336'
                            }}
                        />
                        <span className="timer-text">{timeLeft}s</span>
                    </div>
                )}

                <div className="question-text">
                    {question}
                </div>

                <div className="question-options">
                    {options.map((option, index) => (
                        <button
                            key={index}
                            className={`question-option ${getOptionClass(option)}`}
                            onClick={() => handleAnswer(option)}
                            disabled={showResult}
                        >
                            <span className="option-letter">
                                {String.fromCharCode(65 + index)}
                            </span>
                            <span className="option-text">{option}</span>
                            {showResult && option === correctAnswer && (
                                <span className="option-icon">✓</span>
                            )}
                            {showResult && option === selectedAnswer && !isCorrect && (
                                <span className="option-icon">✗</span>
                            )}
                        </button>
                    ))}
                </div>

                {showResult && (
                    <div className={`question-result ${isCorrect ? 'correct' : 'incorrect'}`}>
                        <div className="result-header">
                            {isCorrect ? (
                                <>
                                    <span className="result-icon">🎉</span>
                                    <span className="result-text">Correct! +{xpBonus} XP</span>
                                </>
                            ) : (
                                <>
                                    <span className="result-icon">😔</span>
                                    <span className="result-text">Not quite!</span>
                                </>
                            )}
                        </div>
                        {explanation && (
                            <p className="result-explanation">{explanation}</p>
                        )}
                    </div>
                )}
            </div>
        </div>
    );
}

export default QuestionOverlay;
